import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  CircularProgress,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategoryIcon from "@mui/icons-material/Category";
import api from "../../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useSnackbar } from "../../providers/SnackbarProvider";

const ViewInterviewQuestionBank = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { gen_id } = location.state || "";

  const [loading, setLoading] = useState(true);
  const [questionBankData, setQuestionBankData] = useState(null);

  const fetchQuestionBankDetails = async () => {
    setLoading(true);
    try {
      const payload = {
        gen_id: gen_id,
        page: 1,
        size: 20,
      };

      const res = await api.post("ViewInterviewQuestionBank", payload);

      if (res?.data?.status === "success") {
        setQuestionBankData(res.data.data);
      } else {
        showSnackbar("error", res?.data?.message || "Failed to fetch interview question bank");
      }
    } catch (error) {
      showSnackbar("error", error?.response?.data?.message || error?.message || "An error occurred while fetching details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gen_id) {
      fetchQuestionBankDetails();
    }
  }, [gen_id]);

  const getStatusColor = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "#2e7d32";
      case "REJECTED":
        return "#c62828";
      case "DRAFT":
        return "#f57c00";
      default:
        return "#757575";
    }
  };

  const getStatusBackgroundColor = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "#e8f5e9";
      case "REJECTED":
        return "#ffebee";
      case "DRAFT":
        return "#fff3e0";
      default:
        return "#f5f5f5";
    }
  };

  if (loading) {
    return <OverAllLoader message="Loading interview question bank details..." />;
  }

  if (!questionBankData) {
    return (
      <Box sx={{ minHeight: "100vh", p: 1 }}>
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#1c97e3",
            gap: "4px",
            cursor: "pointer",
            width: "fit-content",
            mb: 2,
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/interview-question-bank");
            }
          }}
          onClick={() => navigate("/interview-question-bank")}
        >
          <ArrowBackIcon sx={{ fontSize: "17px" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}>
            Back
          </Typography>
        </Grid>
        <Box sx={{ textAlign: "center", mt: 10 }}>
          <Typography sx={{ fontSize: "18px", color: "#757575" }}>
            No interview question bank data found
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {/* Back Button */}
      <Grid
        item
        sx={{
          display: "flex",
          alignItems: "center",
          color: "#1c97e3",
          gap: "4px",
          cursor: "pointer",
          width: "fit-content",
          mb: 2,
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate("/interview-question-bank");
          }
        }}
        onClick={() => navigate("/interview-question-bank")}
      >
        <ArrowBackIcon sx={{ fontSize: "17px" }} />
        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}>
          Back
        </Typography>
      </Grid>

      {/* Header */}
      <Box
        sx={{
          pb: 2,
          display: "flex",
          gap: 1,
          alignItems: "center",
          borderBottom: "1px solid #d1d1d1",
        }}
      >
        <Grid
          sx={{
            backgroundColor: "#1c97e3",
            p: 0.5,
            borderRadius: "10px",
          }}
        >
          <CategoryIcon sx={{ color: "white", fontSize: "35px" }} />
        </Grid>
        <Grid>
          <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
            Interview Question Bank Details
          </Typography>
        </Grid>
      </Box>

      {/* Job Role Information */}
      <Card
        sx={{
          mt: 3,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: "14px", color: "#757575", mb: 0.5 }}>
                Job Role
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a" }}>
                {questionBankData.program_name}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: "14px", color: "#757575", mb: 0.5 }}>
                Interviewer
              </Typography>
              <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a" }}>
                {questionBankData.interviewer_name}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
                {questionBankData.interviewer_email}
              </Typography>
            </Grid>
          </Grid>

          {/* Status Counts */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={`Total: ${questionBankData.counts?.total || 0}`}
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
                fontWeight: 600,
                fontSize: "14px",
              }}
            />
            <Chip
              label={`Accepted: ${questionBankData.counts?.accepted || 0}`}
              sx={{
                backgroundColor: "#e8f5e9",
                color: "#2e7d32",
                fontWeight: 600,
                fontSize: "14px",
              }}
            />
            <Chip
              label={`Rejected: ${questionBankData.counts?.rejected || 0}`}
              sx={{
                backgroundColor: "#ffebee",
                color: "#c62828",
                fontWeight: 600,
                fontSize: "14px",
              }}
            />
            <Chip
              label={`Draft: ${questionBankData.counts?.draft || 0}`}
              sx={{
                backgroundColor: "#fff3e0",
                color: "#f57c00",
                fontWeight: 600,
                fontSize: "14px",
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Questions Section */}
      <Box sx={{ mt: 4 }}>
        <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1a1a1a", mb: 3 }}>
          Questions ({questionBankData.questions?.length || 0})
        </Typography>

        {questionBankData.questions?.map((question, index) => (
          <Card
            key={question.question_id}
            sx={{
              mb: 3,
              borderRadius: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #eef2f7",
              transition: "box-shadow 0.3s ease, transform 0.3s ease",
              "&:hover": {
                boxShadow: "0 8px 24px rgba(28,151,227,0.12)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <CardContent>
              {/* Question Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      minWidth: "36px",
                      height: "36px",
                      borderRadius: "9px",
                      background: "linear-gradient(135deg, #1c97e3 0%, #03438A 100%)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Chip
                    label={question.question_type}
                    size="small"
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: "#1565c0",
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  />
                  {question.experience_level && (
                    <Chip
                      label={question.experience_level}
                      size="small"
                      sx={{
                        backgroundColor: "#f3e5f5",
                        color: "#7b1fa2",
                        fontWeight: 600,
                        fontSize: "12px",
                      }}
                    />
                  )}
                </Box>
                <Chip
                  label={question.status}
                  size="small"
                  sx={{
                    backgroundColor: getStatusBackgroundColor(question.status),
                    color: getStatusColor(question.status),
                    fontWeight: 700,
                    fontSize: "12px",
                    textTransform: "uppercase",
                  }}
                />
              </Box>

              {/* Question */}
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#333",
                  lineHeight: 1.7,
                  mb: 2,
                }}
              >
                {question.question}
              </Typography>

              {/* Answer */}
              {question.answer && (
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #a5d6a7",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "12px",
                      color: "#2e7d32",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1,
                    }}
                  >
                    Answer
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#1b5e20",
                      lineHeight: 1.7,
                    }}
                  >
                    {question.answer}
                  </Typography>
                </Box>
              )}

              {/* Feedback */}
              {question.feedback && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#fff3e0",
                    border: "1px solid #ffcc80",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "13px",
                      color: "#e65100",
                      mb: 0.5,
                    }}
                  >
                    Feedback
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#5d4037",
                      lineHeight: 1.6,
                    }}
                  >
                    {question.feedback}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ViewInterviewQuestionBank;